/**
 * ff-product-purchase.js
 *
 * Drives the FlexComfort product hero: variant selection, one-time/subscription
 * purchase-option toggling, quantity, and Add to Cart / Buy it now submission.
 *
 * Talks to the rest of the landing page (and to any duplicated instance of this
 * section) via namespaced `ff:*` CustomEvents on `document`, and — when available —
 * dispatches Shopify's standardized `shopify:cart:lines-update` event so the
 * theme's own cart drawer / cart count refresh natively instead of us
 * reimplementing them.
 *
 * Idempotent: safe to construct more than once per page (multiple product-hero
 * instances) and re-initializes cleanly after Theme Editor section reloads.
 */
(function () {
  'use strict';

  var CART_ADD_URL = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
  var CART_URL = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';

  /** Best-effort import of Shopify's standardized cart events. Optional — every
   * call site guards for it being unavailable so this never throws. */
  var standardEventsPromise = null;
  function loadStandardEvents() {
    if (!standardEventsPromise) {
      standardEventsPromise = import('@shopify/events').catch(function () {
        return null;
      });
    }
    return standardEventsPromise;
  }

  function money(cents) {
    // Fallback formatter only used if a variant is missing a pre-rendered
    // Liquid money string. Not locale-aware on purpose — the Liquid-rendered
    // strings on each variant are what should normally be used.
    return '$' + (cents / 100).toFixed(2);
  }

  function qs(root, sel) {
    return root.querySelector(sel);
  }
  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  var FFProductForm = /** @class */ (function () {
    function FFProductForm(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.productId = root.dataset.productId;
      this.form = qs(root, 'form[data-ff-product-form]');
      this.variantIdInput = qs(root, '[data-ff-variant-id]');
      this.sellingPlanInput = qs(root, '[data-ff-selling-plan-id]');
      this.qtyInput = qs(root, '[data-ff-qty-input]');
      this.addButton = qs(root, '[data-ff-add-button]');
      this.buyButton = qs(root, '[data-ff-buy-button]');
      this.errorEl = qs(root, '[data-ff-form-error]');
      this.liveRegion = qs(root, '[data-ff-live-region]');

      var dataScript = qs(root, '[data-ff-variant-json]');
      try {
        this.variants = dataScript ? JSON.parse(dataScript.textContent) : [];
      } catch (e) {
        this.variants = [];
      }

      this.isSubmitting = false;
      this.currentVariant = this.getVariantById(this.variantIdInput && this.variantIdInput.value);

      this.onOptionChange = this.onOptionChange.bind(this);
      this.onPurchaseOptionChange = this.onPurchaseOptionChange.bind(this);
      this.onQtyChange = this.onQtyChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.bind();
      this.syncPurchaseState('init');
    }

    FFProductForm.prototype.bind = function () {
      var self = this;
      qsa(this.root, '[data-ff-option-input]').forEach(function (input) {
        input.addEventListener('change', self.onOptionChange);
      });
      qsa(this.root, '[data-ff-purchase-option-input]').forEach(function (input) {
        input.addEventListener('change', self.onPurchaseOptionChange);
      });
      qsa(this.root, '[data-ff-qty-decrease]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.stepQty(-1);
        });
      });
      qsa(this.root, '[data-ff-qty-increase]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.stepQty(1);
        });
      });
      if (this.qtyInput) this.qtyInput.addEventListener('change', this.onQtyChange);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);
    };

    FFProductForm.prototype.destroy = function () {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    };

    FFProductForm.prototype.getVariantById = function (id) {
      id = String(id);
      for (var i = 0; i < this.variants.length; i++) {
        if (String(this.variants[i].id) === id) return this.variants[i];
      }
      return this.variants[0] || null;
    };

    FFProductForm.prototype.getSelectedOptionValues = function () {
      var values = [];
      qsa(this.root, '[data-ff-option-fieldset]').forEach(function (fieldset) {
        var checked = fieldset.querySelector('input:checked');
        if (checked) values.push(checked.value);
      });
      return values;
    };

    FFProductForm.prototype.findMatchingVariant = function () {
      var selected = this.getSelectedOptionValues();
      if (!selected.length) return this.currentVariant;
      for (var i = 0; i < this.variants.length; i++) {
        var v = this.variants[i];
        var match = v.options.every(function (opt, idx) {
          return selected[idx] === undefined || opt === selected[idx];
        });
        if (match) return v;
      }
      return null;
    };

    FFProductForm.prototype.onOptionChange = function () {
      var variant = this.findMatchingVariant();
      if (variant) this.applyVariant(variant);
      this.syncPurchaseState('variant');
    };

    FFProductForm.prototype.applyVariant = function (variant) {
      this.currentVariant = variant;
      if (this.variantIdInput) this.variantIdInput.value = variant.id;

      qsa(this.root, '[data-ff-swatch]').forEach(function (swatch) {
        var isCurrent = swatch.dataset.ffSwatch === variant.options[0];
        swatch.setAttribute('aria-current', isCurrent ? 'true' : 'false');
      });

      // Keep each option fieldset's legend ("Color: Red") in sync with the
      // option value actually selected within that fieldset, since a variant
      // swap can be triggered from a swatch click without a native change
      // event firing on every fieldset.
      qsa(this.root, '[data-ff-option-fieldset]').forEach(function (fieldset) {
        var checked = fieldset.querySelector('input:checked');
        var valueEl = qs(fieldset, '[data-ff-option-value]');
        if (checked && valueEl) valueEl.textContent = checked.value;
      });

      if (variant.featured_image) {
        var mainMedia = qs(this.root, '[data-ff-main-media] img');
        if (mainMedia) {
          mainMedia.src = variant.featured_image;
          mainMedia.srcset = '';
        }
      }

      this.updatePriceDisplay(this.getActivePurchaseOption());
      this.updateAvailability(variant);
    };

    FFProductForm.prototype.getActivePurchaseOption = function () {
      var checked = qs(this.root, '[data-ff-purchase-option-input]:checked');
      return checked ? checked.value : 'one_time';
    };

    FFProductForm.prototype.onPurchaseOptionChange = function () {
      var option = this.getActivePurchaseOption();
      var checkedInput = qs(this.root, '[data-ff-purchase-option-input]:checked');
      var sellingPlanId = checkedInput ? checkedInput.dataset.sellingPlanId : '';

      if (this.sellingPlanInput) {
        if (option === 'subscription' && sellingPlanId) {
          this.sellingPlanInput.value = sellingPlanId;
          this.sellingPlanInput.disabled = false;
        } else {
          this.sellingPlanInput.value = '';
          this.sellingPlanInput.disabled = true;
        }
      }

      this.updatePriceDisplay(option);
      this.syncPurchaseState('selling_plan');
    };

    FFProductForm.prototype.updatePriceDisplay = function (option) {
      var variant = this.currentVariant;
      if (!variant) return;
      var priceEl = qs(this.root, '[data-ff-price]');
      var compareEl = qs(this.root, '[data-ff-compare-price]');
      var saveEl = qs(this.root, '[data-ff-save-badge]');

      var priceHtml = variant.price_html;
      var compareHtml = variant.compare_at_price_html;
      var savePercent = variant.save_percent;

      if (option === 'subscription' && variant.selling_plans && variant.selling_plans.length) {
        var checkedInput = qs(this.root, '[data-ff-purchase-option-input]:checked');
        var planId = checkedInput ? checkedInput.dataset.sellingPlanId : null;
        var plan = variant.selling_plans.filter(function (p) {
          return String(p.id) === String(planId);
        })[0] || variant.selling_plans[0];
        if (plan) {
          priceHtml = plan.price_html;
          compareHtml = plan.compare_at_price_html;
          savePercent = plan.save_percent;
        }
      }

      if (priceEl) priceEl.innerHTML = priceHtml || money(variant.price);
      if (compareEl) {
        if (compareHtml) {
          compareEl.innerHTML = compareHtml;
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      if (saveEl) {
        if (savePercent) {
          saveEl.textContent = 'Save ' + savePercent + '%';
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      }
    };

    FFProductForm.prototype.updateAvailability = function (variant) {
      var available = variant && variant.available;
      if (this.addButton) {
        this.addButton.disabled = !available;
        this.addButton.textContent = available
          ? this.addButton.dataset.addLabel || 'Add to Cart'
          : this.addButton.dataset.soldOutLabel || 'Sold Out';
      }
      if (this.buyButton) this.buyButton.hidden = !available;
    };

    FFProductForm.prototype.stepQty = function (delta) {
      if (!this.qtyInput) return;
      var min = parseInt(this.qtyInput.min, 10) || 1;
      var value = parseInt(this.qtyInput.value, 10) || min;
      value = Math.max(min, value + delta);
      this.qtyInput.value = value;
      this.syncPurchaseState('quantity');
    };

    FFProductForm.prototype.onQtyChange = function () {
      var min = parseInt(this.qtyInput.min, 10) || 1;
      var value = parseInt(this.qtyInput.value, 10) || min;
      this.qtyInput.value = Math.max(min, value);
      this.syncPurchaseState('quantity');
    };

    FFProductForm.prototype.syncPurchaseState = function (reason) {
      var variant = this.currentVariant;
      if (!variant) return;
      var option = this.getActivePurchaseOption();
      var checkedInput = qs(this.root, '[data-ff-purchase-option-input]:checked');
      var sellingPlanId = option === 'subscription' && checkedInput ? checkedInput.dataset.sellingPlanId : null;

      document.dispatchEvent(
        new CustomEvent('ff:purchase-state-change', {
          bubbles: true,
          detail: {
            reason: reason,
            productId: this.productId,
            variantId: variant.id,
            variantTitle: variant.title,
            variantImage: variant.featured_image,
            sellingPlanId: sellingPlanId,
            quantity: this.qtyInput ? parseInt(this.qtyInput.value, 10) || 1 : 1,
            available: variant.available,
            priceHtml: qs(this.root, '[data-ff-price]') ? qs(this.root, '[data-ff-price]').innerHTML : '',
            sourceSectionId: this.sectionId,
          },
        })
      );

      document.dispatchEvent(
        new CustomEvent('ff:variant-change', {
          bubbles: true,
          detail: { productId: this.productId, variantId: variant.id, sourceSectionId: this.sectionId },
        })
      );
      if (reason === 'selling_plan') {
        document.dispatchEvent(
          new CustomEvent('ff:selling-plan-change', {
            bubbles: true,
            detail: { productId: this.productId, sellingPlanId: sellingPlanId, sourceSectionId: this.sectionId },
          })
        );
      }
    };

    FFProductForm.prototype.showError = function (message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.classList.add('is-visible');
      if (this.liveRegion) this.liveRegion.textContent = message;
    };

    FFProductForm.prototype.clearError = function () {
      if (!this.errorEl) return;
      this.errorEl.classList.remove('is-visible');
      this.errorEl.textContent = '';
    };

    FFProductForm.prototype.onSubmit = function (evt) {
      var submitter = evt.submitter;
      var isCheckout = submitter && submitter.name === 'checkout';
      if (isCheckout) return; // let the browser submit natively straight to checkout

      evt.preventDefault();
      if (this.isSubmitting) return;
      this.addToCart();
    };

    FFProductForm.prototype.addToCart = function () {
      var self = this;
      if (!this.currentVariant) return;
      this.isSubmitting = true;
      this.clearError();

      var addLabel = this.addButton ? this.addButton.textContent : '';
      if (this.addButton) {
        this.addButton.disabled = true;
        this.addButton.textContent = this.addButton.dataset.addingLabel || 'Adding…';
      }

      var quantity = this.qtyInput ? parseInt(this.qtyInput.value, 10) || 1 : 1;
      var payload = { id: this.currentVariant.id, quantity: quantity };
      if (this.sellingPlanInput && !this.sellingPlanInput.disabled && this.sellingPlanInput.value) {
        payload.selling_plan = this.sellingPlanInput.value;
      }

      var resolveStandardEvent = null;
      loadStandardEvents().then(function (mod) {
        if (!mod || !mod.CartLinesUpdateEvent) return;
        try {
          var created = mod.CartLinesUpdateEvent.createPromise();
          resolveStandardEvent = created.resolve;
          document.dispatchEvent(
            new mod.CartLinesUpdateEvent({ action: 'add', lines: [payload], promise: created.promise })
          );
        } catch (e) {
          /* Standard events are a best-effort enhancement; ignore failures. */
        }
      });

      fetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (res) {
          return res.json().then(function (json) {
            return { ok: res.ok, json: json };
          });
        })
        .then(function (result) {
          self.isSubmitting = false;
          if (!result.ok) {
            var message = result.json && result.json.description ? result.json.description : 'Something went wrong adding this to your cart.';
            self.showError(message);
            if (self.addButton) {
              self.addButton.disabled = false;
              self.addButton.textContent = addLabel;
            }
            if (resolveStandardEvent) resolveStandardEvent({ detail: { didError: true } });
            document.dispatchEvent(
              new CustomEvent('ff:cart-add-error', { bubbles: true, detail: { message: message, sourceSectionId: self.sectionId } })
            );
            return;
          }

          if (self.addButton) {
            self.addButton.disabled = false;
            self.addButton.textContent = self.addButton.dataset.addLabel || 'Add to Cart';
            self.addButton.classList.add('is-added');
            setTimeout(function () {
              self.addButton.classList.remove('is-added');
            }, 1600);
          }
          if (self.liveRegion) self.liveRegion.textContent = 'Added to cart';

          if (resolveStandardEvent) resolveStandardEvent({ detail: { didError: false, cart: result.json } });

          document.dispatchEvent(
            new CustomEvent('ff:cart-add-success', {
              bubbles: true,
              detail: {
                productId: self.productId,
                variantId: self.currentVariant.id,
                quantity: quantity,
                sellingPlanId: payload.selling_plan || null,
                sourceSectionId: self.sectionId,
                line: result.json,
              },
            })
          );

          // Fallback refresh for themes without a cart drawer wired to the
          // standardized event above: bump any visible cart-count bubble.
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch(function () {
          self.isSubmitting = false;
          if (self.addButton) {
            self.addButton.disabled = false;
            self.addButton.textContent = addLabel;
          }
          self.showError('We couldn’t reach the cart. Please check your connection and try again.');
          if (resolveStandardEvent) resolveStandardEvent({ detail: { didError: true } });
        });
    };

    return FFProductForm;
  })();

  function init(root) {
    if (root.__ffProductForm) return;
    root.__ffProductForm = new FFProductForm(root);
  }

  function initAll() {
    qsa(document, '[data-ff-product-hero]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    qsa(evt.target, '[data-ff-product-hero]').forEach(function (root) {
      root.__ffProductForm = null;
      init(root);
    });
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    qsa(evt.target, '[data-ff-product-hero]').forEach(function (root) {
      if (root.__ffProductForm) {
        root.__ffProductForm.destroy();
        root.__ffProductForm = null;
      }
    });
  });
})();
